<template>
  <section class="product-section" :class="sectionClasses" :style="sectionStyle">
    <div class="container">
      <!-- Banner Bar (shows with gradient/color, no image needed) -->
      <div class="section-content-banner">
        <div class="section-banner-bar" :style="bannerBarStyle">
          <div class="banner-bar-left">
            <span class="banner-bar-title">{{ title }}</span>
            <span v-if="bannerDescription" class="banner-bar-separator">|</span>
            <span v-if="bannerDescription" class="banner-bar-desc">{{ bannerDescription }}</span>
          </div>
          <a v-if="viewAllLink" :href="viewAllLink" class="banner-bar-link">
            {{ viewAllText }} <i class="fas fa-chevron-right"></i>
          </a>
        </div>

        <!-- Products Grid: 6 per line PC, 2 per line mobile -->
        <div class="section-products">
          <div v-if="loading" class="section-loading">
            <div class="spinner"></div>
            <p>Loading products...</p>
          </div>

          <div v-else-if="products.length === 0" class="section-empty">
            <p>No products available</p>
          </div>

          <div v-else class="products-grid">
            <product-card
              v-for="product in displayedProducts"
              :key="product.id"
              :product="product"
              :show-save-badge="showSaveBadge"
              :compact="compact"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import ProductCard from './ProductCard.vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  products: {
    type: Array,
    default: () => []
  },
  viewAllLink: {
    type: String,
    default: null
  },
  viewAllText: {
    type: String,
    default: 'See All'
  },
  maxItems: {
    type: Number,
    default: 6
  },
  loading: {
    type: Boolean,
    default: false
  },
  backgroundColor: {
    type: String,
    default: '#ffffff'
  },
  bgGradient: {
    type: String,
    default: null
  },
  hasBanner: {
    type: Boolean,
    default: false
  },
  bannerDescription: {
    type: String,
    default: null
  },
  bannerImage: {
    type: String,
    default: null
  },
  bannerColor: {
    type: String,
    default: '#1e3a5f'
  },
  showSaveBadge: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  sectionType: {
    type: String,
    default: 'default'
  }
})

const effectiveMaxItems = computed(() => {
  return 12
})

const displayedProducts = computed(() => {
  return props.products.slice(0, effectiveMaxItems.value)
})

const sectionClasses = computed(() => ({
  [`section-${props.sectionType}`]: props.sectionType && props.sectionType !== 'default'
}))


const sectionStyle = computed(() => {
  if (props.bgGradient) {
    return {
      background: props.bgGradient
    }
  }
  return {
    backgroundColor: props.backgroundColor
  }
})

// Tailwind color map for gradient parsing
const tailwindColors = {
  'orange-500': '#f97316', 'orange-600': '#ea580c', 'orange-700': '#c2410c',
  'red-500': '#ef4444', 'red-600': '#dc2626', 'red-700': '#b91c1c',
  'blue-500': '#3b82f6', 'blue-600': '#2563eb', 'blue-700': '#1d4ed8',
  'green-500': '#22c55e', 'green-600': '#16a34a', 'green-700': '#15803d',
  'purple-500': '#a855f7', 'purple-600': '#9333ea', 'purple-700': '#7e22ce',
  'pink-500': '#ec4899', 'pink-600': '#db2777', 'pink-700': '#be185d',
  'yellow-500': '#eab308', 'yellow-600': '#ca8a04', 'yellow-700': '#a16207',
  'teal-500': '#14b8a6', 'teal-600': '#0d9488', 'teal-700': '#0f766e',
  'indigo-500': '#6366f1', 'indigo-600': '#4f46e5', 'indigo-700': '#4338ca',
  'gray-500': '#6b7280', 'gray-600': '#4b5563', 'gray-700': '#374151',
  'black': '#000000', 'white': '#ffffff'
}

const parseTailwindGradient = (gradientString) => {
  if (!gradientString) return null

  // Handle direct CSS gradients
  if (gradientString.includes('linear-gradient') || gradientString.includes('radial-gradient')) {
    return gradientString
  }

  // Handle hex colors directly
  if (gradientString.includes('#')) {
    return gradientString
  }

  // Handle Tailwind gradient syntax (with or without spaces)
  const fromMatch = gradientString.match(/from-([a-z]+-\d+|black|white)/)
  const toMatch = gradientString.match(/to-([a-z]+-\d+|black|white)/)

  if (fromMatch && toMatch) {
    const fromColor = tailwindColors[fromMatch[1]] || fromMatch[1]
    const toColor = tailwindColors[toMatch[1]] || toMatch[1]

    // If colors are still Tailwind names, convert to fallback colors
    const finalFromColor = tailwindColors[fromColor] || fromColor || '#3b82f6'
    const finalToColor = tailwindColors[toColor] || toColor || '#1d4ed8'

    return `linear-gradient(90deg, ${finalFromColor} 0%, ${finalToColor} 100%)`
  }

  return null
}

const bannerBarStyle = computed(() => {
  const parsedGradient = parseTailwindGradient(props.bgGradient)
  if (parsedGradient) {
    return { background: parsedGradient }
  }
  return {
    background: `linear-gradient(90deg, ${props.bannerColor} 0%, ${props.bannerColor}dd 100%)`
  }
})
</script>
